import React, { useState, useRef, useEffect, ReactNode } from "react";

import classNames from "classnames";

import { tryConvertHeicToJpeg } from "src/utils/heicRecovery";

import "./ImagesChecker.scss";

interface Props {
  data: { [key: string]: string }[];
  accessorKey: string;
  max?: number;
  noImagesFallback: ReactNode;
  imageClassName?: string;
  /**
   * TikTok cover/thumbnail URLs served via the feed endpoint (not the single-post one) can come back
   * as HEIC, which most non-Safari browsers can't render natively.
   * See https://app.asana.com/1/242052987772680/project/807220246535156/task/1217546080711977?focus=true
   * Opt-in only, so an onError on a genuinely dead link elsewhere doesn't pay for an extra fetch.
   */
  allowHeicRecovery?: boolean;
  /** Downscale a recovered HEIC->JPEG to this longest-side size - only applied if the source is bigger. */
  heicRecoveryMaxDimension?: number;
}

function ImagesChecker(props: Props) {
  const {
    data,
    accessorKey,
    max = 3,
    noImagesFallback,
    imageClassName,
    allowHeicRecovery,
    heicRecoveryMaxDimension,
  } = props;

  const [visibleCount, setVisibleCount] = useState(1);
  const [failedUrls, setFailedUrls] = useState<string[]>([]);
  const [visibleUrls, setVisibleUrls] = useState<string[]>([]);
  const [convertedUrls, setConvertedUrls] = useState<Record<string, string>>(
    {},
  );
  const triedHeicConversion = useRef<Set<string>>(new Set());
  const createdObjectUrls = useRef<string[]>([]);
  const loadedCount = useRef(0);

  // Converted HEIC->JPEG blobs are held in memory via their object URL until revoked -
  // otherwise browsing many TikTok rows in Creator Search leaks memory across route changes.
  // createdObjectUrls is a plain accumulator ref (push-only, never reassigned), so reading
  // .current at unmount time is intentional - it must see every URL created since mount.
  useEffect(() => {
    const objectUrls = createdObjectUrls;

    return () => {
      objectUrls.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const tryNextImage = () => {
    if (loadedCount.current < max && visibleCount <= data.length) {
      setVisibleCount((prev) => prev + 1);
    }
  };

  const handleLoad = (url: string) => {
    loadedCount.current += 1;
    setVisibleUrls((prev) => [...prev, url]);
    if (loadedCount.current >= max) return;
    tryNextImage();
  };

  const handleError = async (url: string) => {
    if (allowHeicRecovery && !triedHeicConversion.current.has(url)) {
      triedHeicConversion.current.add(url);

      const convertedSrc = await tryConvertHeicToJpeg(url, {
        maxDimension: heicRecoveryMaxDimension,
      });

      if (convertedSrc) {
        createdObjectUrls.current.push(convertedSrc);
        setConvertedUrls((prev) => ({ ...prev, [url]: convertedSrc }));

        return;
      }
    }

    setFailedUrls((prev) => [...prev, url]);
    tryNextImage();
  };

  const hasNoWorkingImages = failedUrls.length === data.length;

  return (
    <>
      {data.slice(0, visibleCount).map((item, index) => {
        const url = item[accessorKey];
        const isFailed = failedUrls.includes(url);

        if (isFailed) return null;
        const isVisible = visibleUrls.includes(url);

        return (
          <img
            key={`${item[accessorKey]}-${index}`}
            src={convertedUrls[url] || item[accessorKey]}
            alt={`Post ${index + 1}`}
            className={classNames(
              "images-checker-item",
              {
                "images-checker-item--loading": !isVisible,
              },
              imageClassName,
            )}
            referrerPolicy="no-referrer"
            onLoad={() => handleLoad(url)}
            onError={() => handleError(url)}
            loading="lazy"
          />
        );
      })}

      {noImagesFallback && hasNoWorkingImages ? noImagesFallback : null}
    </>
  );
}

export default ImagesChecker;
